import { Grid } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import { FormikInputSelect, FormikInputString, FormikInputZip, Modal, P2 } from 'src/components';
import { useDispatch, useSelector } from 'react-redux';
import { createYard, saveYard } from 'src/redux/yards/actions';
import {
  selectCreateYardErrors,
  selectCreateYardLoading,
  selectCreateYardSuccess,
  selectSaveYardErrors,
  selectSaveYardSuccess,
} from 'src/redux/yards/selectors';
import { useEffect } from 'react';
import { YardType } from 'src/types';
import config from 'src/config';

type DialogProps = {
  onClose: () => void;
  isEdit?: boolean;
  yard?: YardType;
};

const AddCompanyYardDialog = ({ onClose, isEdit = false, yard }: DialogProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const dispatchTo = isEdit ? saveYard : createYard;
  const yardSuccess = useSelector(isEdit ? selectSaveYardSuccess : selectCreateYardSuccess);
  const isCreateYardLoading = useSelector(selectCreateYardLoading);
  const yardErrors = useSelector(isEdit ? selectSaveYardErrors : selectCreateYardErrors) as Record<string, string[]>;

  const yardsValidationSchema = Yup.object({
    name: Yup.string().required(i18next.t('validations.required')),
    country: Yup.string().required(i18next.t('validations.required')),
    city: Yup.string().required(i18next.t('validations.required')),
    street: Yup.string().required(i18next.t('validations.required')),
    state: Yup.string().required(i18next.t('validations.required')),
    zipCode: Yup.string().required(i18next.t('validations.required')),
  });

  const formik = useFormik({
    initialValues: {
      name: yard?.name || '',
      country: yard?.country || '',
      city: yard?.city || '',
      street: yard?.street || '',
      state: yard?.state || '',
      zipCode: yard?.zipCode || '',
    },
    validationSchema: yardsValidationSchema,
    onSubmit: async (data) => {
      dispatch(isEdit ? saveYard.init({ id: yard?.id, ...data }) : createYard.init({ ...data }));
    },
  });

  const { country } = formik.values;
  const states = country ? (country === 'Canada' ? config.options.STATES.CA : config.options.STATES.US) : [];

  useEffect(() => {
    if (yardSuccess) {
      formik.resetForm();
      dispatch(dispatchTo.reset());
      onClose();
    }
  }, [yardSuccess]);

  return (
    <Modal
      title={isEdit ? t('companyYardsPage.editCompanyYard') : t('companyYardsPage.addCompanyYard')}
      okText={t('common.save')}
      onClose={onClose}
      onOk={() => formik.handleSubmit()}
      loading={isCreateYardLoading}
      open
    >
      <Grid container rowSpacing={0} columnSpacing={3}>
        <Grid item xs={12}>
          <FormikInputString formik={formik} field="name" label={t('companyYardsPage.yardName')} autoFocus />
        </Grid>

        <Grid item xs={12} sx={{ mb: 1 }}>
          <P2 sx={{ fontWeight: 600 }}>{t('companyYardsPage.address')}</P2>
        </Grid>
        <Grid item xs={6}>
          <form autoComplete="off">
            <FormikInputSelect
              formik={formik}
              field="country"
              label={t('companyYardsPage.country')}
              options={config.options.COUNTRY.map((country) => ({ key: country, value: country }))}
            />
          </form>
        </Grid>
        <Grid item xs={6}>
          <FormikInputString formik={formik} field="city" label={t('companyYardsPage.city')} />
        </Grid>
        <Grid item xs={12}>
          <FormikInputString formik={formik} field="street" label={t('companyYardsPage.street')} />
        </Grid>
        <Grid item xs={6}>
          <form autoComplete="off">
            <FormikInputSelect
              formik={formik}
              field="state"
              label={country === 'Canada' ? 'Province' : t('companyYardsPage.state')}
              disabled={!country}
              options={states.map((state: string) => ({
                key: state,
                value: t(`${country === 'Canada' ? 'provinces' : 'states'}.${state}`),
              }))}
            />
          </form>
        </Grid>
        <Grid item xs={6}>
          <FormikInputZip formik={formik} field="zipCode" label={t('companyYardsPage.zip')} errorHelper={yardErrors} />
        </Grid>
      </Grid>
    </Modal>
  );
};

export default AddCompanyYardDialog;
